import {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
} from 'react'
import { Link, NavLink } from 'react-router-dom'

import {
  NavigationGroup,
  NavigationItemInterface,
  desktopNavPillSeparatorClass,
} from '@/layouts/partials/NavigationGroup'
import { useWindowSize } from '@/lib/hooks/useWindowSize'
import { useOnClickOutside } from '@/lib/hooks/useOnClickOutside'
import { useApp } from '@/lib/provider/AppProvider'
import { useAuth } from '@/lib/provider/AuthProvider'
import { LEGAL_LINKS, NAVIGATION_LINKS } from '@/lib/definitions/constants'

import AdjustmentsIcon from '@icons/adjustments-vertical'
import LogoutIcon from '@icons/arrow-right-on-rectangle'
import MenuIcon from '@icons/bars-3'
import CogIcon from '@icons/cog'
import ProfileIcon from '@icons/user-circle'
import CloseIcon from '@icons/x-mark'
import ArrowDownIcon from '@icons/chevron-down'
import PhoneIcon from '@icons/phone'
import AtSymbolIcon from '@icons/at-symbol'
import DocumentTextIcon from '@icons/document-text'
import DropdownMenu from '@/layouts/partials/DropdownMenu'
import StatusIndicator from '@/layouts/partials/StatusIndicator'

const CommandPalette = lazy(() => import('./CommandPalette'))
import { createPortal } from 'react-dom'
import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'
import { cn } from '@/lib/misc/Utils'

const ProfileSection = ({
  user,
  onLinkClick,
  isUserAnalyticsEnabled,
  onClose,
  isMobile = false,
}: {
  user: any
  onLinkClick?: () => void
  isUserAnalyticsEnabled?: boolean
  onClose?: () => void
  isMobile?: boolean
}) => {
  const { userInitials, userFullName } = useMemo(() => {
    const email =
      user.email != null && typeof user.email === 'string' ? user.email : ''
    const emailLocal = email ? email.split('@')[0] : ''
    const userFullName: string =
      user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : emailLocal || email
    const userInitials =
      !!user.first_name && !!user.last_name
        ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`
        : (email || user.first_name || user.last_name || '')
            .charAt(0)
            .toUpperCase()
    return { userInitials, userFullName }
  }, [user.email, user.first_name, user.last_name])

  const profileMenuLinkClass =
    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-neutral-60 transition-colors hover:bg-neutral-10/50'

  const profileMenuSignOutClass =
    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50'

  const profileMenuIconClass =
    'flex size-4 shrink-0 items-center justify-center text-neutral-50'

  if (isMobile) {
    return (
      <div className="bg-white/10 rounded-lg p-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-white font-semibold text-lg">
            {userInitials}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white truncate">
              {userFullName}
            </h3>
            <p className="text-sm text-white/70 truncate">{user.email}</p>
          </div>
        </div>

        {user.company?.name && (
          <div className="mb-4 p-3 bg-white/20 rounded-lg">
            <p className="text-xs font-medium text-white/70 mb-1">Company</p>
            <p className="text-white font-medium">{user.company.name}</p>
            {user.company.cid && (
              <p className="text-xs text-white/70 mt-1">{user.company.cid}</p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Link
            data-cy="menu-profile"
            to="/user-profile"
            onClick={() => {
              onLinkClick?.()
              onClose?.()
            }}
            className="flex items-center space-x-3 px-3 py-3 text-white/90 hover:bg-white/20 rounded-lg transition-colors font-medium"
          >
            <ProfileIcon className="w-5 h-5" />
            <span>My Profile</span>
          </Link>

          {isUserAnalyticsEnabled && (
            <Link
              to="/settings/analytics"
              onClick={() => {
                onLinkClick?.()
                onClose?.()
              }}
              className="flex items-center space-x-3 px-3 py-3 text-white/90 hover:bg-white/20 rounded-lg transition-colors font-medium"
            >
              <AdjustmentsIcon className="w-5 h-5" />
              <span>Analytics</span>
            </Link>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-w-[280px] overflow-hidden rounded-xl border border-neutral-20 bg-white shadow-lg">
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-10 text-sm font-semibold text-neutral-60">
            {userInitials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-neutral-60">
              {userFullName}
            </p>
            <p className="truncate text-sm text-neutral-50">{user.email}</p>
          </div>
        </div>

        {user.company?.name && (
          <div className="mt-3 rounded-lg bg-neutral-10/50 p-3">
            <p className="mb-1 text-xs font-medium text-neutral-50">Company</p>
            <p className="text-neutral-60">{user.company.name}</p>
            {user.company.cid && (
              <p className="mt-1 text-xs text-neutral-50">{user.company.cid}</p>
            )}
          </div>
        )}
      </div>

      <div
        className="border-t border-neutral-20"
        role="separator"
        aria-hidden
      />

      <div className="flex flex-col gap-0.5 p-2">
        <Link
          data-cy="menu-profile"
          to="/user-profile"
          onClick={() => {
            onLinkClick?.()
            onClose?.()
          }}
          className={profileMenuLinkClass}
        >
          <span className={profileMenuIconClass}>
            <ProfileIcon className="size-4" />
          </span>
          <span>My Profile</span>
        </Link>

        {isUserAnalyticsEnabled && (
          <Link
            to="/settings/analytics"
            onClick={() => {
              onLinkClick?.()
              onClose?.()
            }}
            className={profileMenuLinkClass}
          >
            <span className={profileMenuIconClass}>
              <AdjustmentsIcon className="size-4" />
            </span>
            <span>Analytics</span>
          </Link>
        )}
      </div>

      <div
        className="border-t border-neutral-20"
        role="separator"
        aria-hidden
      />

      <div className="p-2">
        <Link
          to="/logout"
          onClick={onLinkClick}
          className={profileMenuSignOutClass}
        >
          <span className="flex size-4 shrink-0 items-center justify-center text-red-600">
            <LogoutIcon className="size-4" />
          </span>
          <span>Sign Out</span>
        </Link>
      </div>
    </div>
  )
}

const Navigation = () => {
  const [appState] = useApp()
  const [auth] = useAuth()
  const [innerWidth] = useWindowSize()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  useOnClickOutside(userMenuRef, () => setIsUserMenuOpen(false))
  const isBenocsEnabled = useFeatureFlag(FLAGS.BENOCS)
  const isMultiTenancyEnabled = useFeatureFlag(FLAGS.MULTI_TENANCY)
  const isTicketsEnabled = useFeatureFlag(FLAGS.TICKETS)

  const userProfileName: string = useMemo(
    () => auth.user.first_name || auth.user.last_name || auth.user.email,
    [auth.user]
  )

  const settingsHref: string = useMemo(
    () =>
      auth.user.customer_id ? '/settings/users' : '/settings/create-customer',
    [auth.user.customer_id]
  )

  const navPillClass =
    'flex shrink-0 items-center gap-1 rounded-full border border-white/15 bg-white/5 p-1'

  const desktopNavPillItemBaseClass =
    'group flex shrink-0 cursor-pointer flex-row items-center rounded-full px-3 text-gray-300 transition-colors hover:bg-gray-200 hover:text-gray-700 sm:py-1.5'

  const desktopNavPillItemCompactClass = cn(
    desktopNavPillItemBaseClass,
    'space-x-1'
  )

  const desktopNavPillItemActiveClass = 'bg-white/10'

  // customer asn
  const customerAsn = useMemo(() => auth.user.company?.asn, [auth.user])

  const mainMenuItems: NavigationItemInterface[] = useMemo(
    () => [
      { id: 'dashboard', to: '/', title: 'Dashboard' },
      {
        id: 'customers',
        to: '/customers',
        title: 'Customers',
        isHidden:
          !isMultiTenancyEnabled || !auth.user.company?.cid?.startsWith('R'), // maybe use Customer.is_reseller() and get this info from the backend
        isActive: window.location.pathname.startsWith('/settings'),
      },
      ...(isBenocsEnabled
        ? [
            {
              id: 'insights',
              to: '/insights',
              title: 'Insights',
              external: true,
              showArrow: false,
            },
          ]
        : []),
      {
        id: 'billing',
        to: '/billing',
        title: 'Billing',
      },
      {
        id: 'tickets',
        to: '/tickets',
        title: 'Tickets',
        isHidden: !isTicketsEnabled,
      },
      {
        id: 'tools',
        title: 'Tools',
        links: [
          {
            id: 'api',
            label: 'API',
            to: `${import.meta.env.VITE_API_DOCS_URL}`,
            external: true,
          },
          {
            id: 'knowledge-base',
            label: 'Knowledge Base',
            to: NAVIGATION_LINKS.KNOWLEDGE_BASE,
            external: true,
          },
          {
            id: 'looking-glass',
            label: 'Looking Glass',
            to: NAVIGATION_LINKS.LOOKING_GLASS,
            external: true,
          },
          {
            id: 'bgp-communities',
            label: 'BGP Communities',
            to: NAVIGATION_LINKS.BGP_COMMUNITIES,
            external: true,
          },
          {
            id: 'map',
            label: 'Network Map',
            to: LEGAL_LINKS.NETWORK_MAP,
            external: true,
            isSeparated: true,
          },
          {
            id: 'peeringdb-as5405',
            label: 'PeeringDB AS5405',
            to: NAVIGATION_LINKS.PEERINGDB_AS5405,
            external: true,
            isSeparated: true,
          },
          {
            id: 'bgp-tools-as5405',
            label: 'BGP.tools AS5405',
            to: NAVIGATION_LINKS.BGP_TOOLS_AS5405,
            external: true,
          },

          ...(!!customerAsn
            ? [
                {
                  id: 'peeringdb-customer-as',
                  label: `PeeringDB AS${customerAsn}`,
                  to: `https://www.peeringdb.com/asn/${customerAsn}`,
                  external: true,
                },
                {
                  id: 'bgp-tools-customer-as',
                  label: `BGP.tools AS${customerAsn}`,
                  to: `https://bgp.tools/as/${customerAsn}`,
                  external: true,
                },
              ]
            : []),

          {
            id: 'changelog',
            label: 'Changelog',
            to: NAVIGATION_LINKS.CHANGELOG,
            external: true,
            isSeparated: true,
          },
        ],
        alignment: 'left',
      },
    ],
    [
      isMultiTenancyEnabled,
      isBenocsEnabled,
      isTicketsEnabled,
      auth.user,
      customerAsn,
    ]
  )

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen)

  const toggleUserMenu = useCallback(() => {
    setIsUserMenuOpen((open) => !open)
  }, [])

  const toggleCommandPalette = useCallback(() => {
    setIsCommandPaletteOpen((open) => !open)
  }, [])

  const isCommandPaletteEnabled = useMemo(
    () =>
      auth.user.show_company_info &&
      appState.waffleFlags?.includes('superuser_command_palette'),
    [appState, auth.user]
  )

  const isUserAnalyticsEnabled = useFeatureFlag(FLAGS.USER_ANALYTICS)

  useEffect(() => {
    if (!isCommandPaletteEnabled) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        auth.user.show_company_info &&
        (event.metaKey || event.ctrlKey) &&
        event.key === 'k'
      ) {
        event.preventDefault()
        setIsCommandPaletteOpen(!isCommandPaletteOpen)
      } else if (event.key === 'Escape' && isCommandPaletteOpen) {
        setIsCommandPaletteOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    auth.user.show_company_info,
    isCommandPaletteOpen,
    isCommandPaletteEnabled,
  ])

  // Prevent body scroll when mobile menu is open and handle escape key
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false)
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.body.style.overflow = 'unset'
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isMobileMenuOpen])

  return (
    <>
      <nav className="h-14 w-full z-20 box-content">
        <div className="relative flex h-14 w-full min-w-0 items-center justify-between">
          {innerWidth < 768 ? (
            <>
              <div className="bg-brand-1 h-14 absolute left-0 top-0 w-full">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"
                >
                  <img src="/images/logo-wide.svg" alt="Inter.link Logo" />
                </Link>
              </div>

              {isMobileMenuOpen ? (
                <CloseIcon
                  className="absolute top-4 z-60 cursor-pointer text-2xl text-white left-[max(1rem,env(safe-area-inset-left,0px))]"
                  onClick={toggleMobileMenu}
                />
              ) : (
                <MenuIcon
                  className="absolute top-4 z-60 cursor-pointer text-2xl text-white left-[max(1rem,env(safe-area-inset-left,0px))]"
                  onClick={toggleMobileMenu}
                />
              )}

              <div className="absolute top-1/2 z-60 -translate-y-1/2 right-[max(1.25rem,env(safe-area-inset-right,0px))]">
                <StatusIndicator variant="dot" withSupport />
              </div>

              {isMobileMenuOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 top-14"
                    onClick={toggleMobileMenu}
                  />

                  <div
                    className={`fixed inset-y-0 left-0 top-14 w-full max-w-sm bg-brand-1 z-50 transform transition-transform duration-300 ease-in-out border-t border-white/20 ${
                      isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex-1 overflow-y-auto">
                        <div className="px-4 py-6">
                          {mainMenuItems
                            .filter((item) => !item.isHidden)
                            .map((item, index) => {
                              const mobileMainItemClass =
                                index > 0
                                  ? 'mt-3 border-t border-white/15 pt-3'
                                  : undefined

                              return (
                                <div
                                  key={item.id || index}
                                  className={mobileMainItemClass}
                                >
                                  {item.links ? (
                                    <DropdownMenu
                                      width={280}
                                      id={item.id}
                                      links={item.links}
                                      alignment="left"
                                    >
                                      <div className="w-full flex items-center justify-between px-4 py-3 text-left text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer">
                                        <span className="font-medium">
                                          {item.title}
                                        </span>
                                        <ArrowDownIcon className="w-5 h-5 text-white/70" />
                                      </div>
                                    </DropdownMenu>
                                  ) : (
                                    <NavLink
                                      to={item.to || '#'}
                                      target={
                                        item.external ? '_blank' : '_self'
                                      }
                                      onClick={toggleMobileMenu}
                                      className={({ isActive }) =>
                                        `block px-4 py-3 text-white hover:bg-white/10 rounded-lg transition-colors font-medium ${
                                          isActive || item.isActive
                                            ? 'bg-white/20 text-white'
                                            : ''
                                        }`
                                      }
                                    >
                                      {item.title}
                                    </NavLink>
                                  )}
                                </div>
                              )
                            })}
                        </div>

                        <div className="border-t border-white/20 mx-4" />

                        <div className="px-4 py-6">
                          <ProfileSection
                            user={auth.user}
                            onLinkClick={toggleMobileMenu}
                            isUserAnalyticsEnabled={isUserAnalyticsEnabled}
                            onClose={toggleMobileMenu}
                            isMobile
                          />
                        </div>

                        <div className="px-4 pb-6">
                          <div className="bg-white/10 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-white mb-3">
                              Support
                            </h3>
                            <div className="space-y-3">
                              <a
                                href="tel:+4930577037417"
                                className="flex items-center space-x-3 text-white/90 hover:text-white transition-colors"
                              >
                                <PhoneIcon className="w-5 h-5" />
                                <span>+49 30 5770 374 17</span>
                              </a>
                              <a
                                href="mailto:support@inter.link"
                                className="flex items-center space-x-3 text-white/90 hover:text-white transition-colors"
                              >
                                <AtSymbolIcon className="w-5 h-5" />
                                <span>support@inter.link</span>
                              </a>
                              <a
                                href="/assets/20250403-Inter.link_Escalation_matrix.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center space-x-3 text-white/90 hover:text-white transition-colors"
                              >
                                <DocumentTextIcon className="w-5 h-5" />
                                <span>Escalation Matrix</span>
                              </a>
                            </div>
                          </div>
                        </div>

                        <div className="px-4 pb-8">
                          <Link
                            to="/logout"
                            onClick={toggleMobileMenu}
                            className="flex items-center space-x-3 px-4 py-3 text-white hover:bg-white/10 rounded-lg transition-colors font-medium"
                          >
                            <LogoutIcon className="w-5 h-5" />
                            <span>Sign Out</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex flex-row lg:grid h-14 w-full min-w-0 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-2 sm:gap-1 sm:px-3">
              {/* Left side of the navigation */}
              <div className="flex min-w-0 items-center justify-start gap-1">
                <div
                  className={cn(
                    navPillClass,
                    'min-w-0 max-w-full p-0 px-[3px]'
                  )}
                >
                  <Link to="/" className="flex h-9 shrink-0 items-center">
                    <img src="/images/logo-wide.svg" alt="Inter.link Logo" />
                  </Link>
                  <StatusIndicator
                    variant="desktop"
                    withSupport
                    className="ml-0 min-w-0 sm:ml-1"
                  />
                </div>
              </div>

              {/* Center of the navigation */}
              <div className="flex min-w-0 justify-center flex-1">
                <div
                  className={cn(
                    navPillClass,
                    'max-w-full min-w-0 shrink justify-center gap-0'
                  )}
                >
                  <div className="flex h-full min-w-0 max-w-full flex-1 items-center overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <NavigationGroup
                      group={{
                        className: 'mr-0',
                        items: mainMenuItems,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Right side of the navigation */}
              <div
                className="relative flex min-w-0 justify-end"
                ref={userMenuRef}
              >
                <div className={cn(navPillClass, 'gap-0 overflow-hidden')}>
                  {isCommandPaletteEnabled && (
                    <>
                      <button
                        type="button"
                        onClick={toggleCommandPalette}
                        className={cn(
                          desktopNavPillItemCompactClass,
                          isCommandPaletteOpen && desktopNavPillItemActiveClass
                        )}
                      >
                        <span className="max-w-[140px] truncate">
                          {auth.user.company?.name ?? 'N/A'}
                        </span>
                      </button>
                      <span
                        className={desktopNavPillSeparatorClass}
                        aria-hidden
                      />
                    </>
                  )}
                  <NavLink
                    data-cy="settings-menu-button"
                    to={settingsHref}
                    className={({ isActive }) =>
                      cn(
                        desktopNavPillItemCompactClass,
                        isActive && desktopNavPillItemActiveClass,
                        isCommandPaletteEnabled &&
                          'p-1! lg:px-3 items-center justify-center'
                      )
                    }
                  >
                    <CogIcon
                      className={cn(
                        isCommandPaletteEnabled ? 'size-5 lg:hidden' : 'hidden'
                      )}
                    />
                    <span
                      className={cn(
                        'h-4 items-center',
                        isCommandPaletteEnabled
                          ? 'hidden lg:inline-flex'
                          : 'inline-flex'
                      )}
                    >
                      Settings
                    </span>
                  </NavLink>
                  <span className={desktopNavPillSeparatorClass} aria-hidden />
                  <button
                    type="button"
                    data-cy="user-menu-button"
                    onClick={toggleUserMenu}
                    className={cn(
                      desktopNavPillItemBaseClass,
                      'gap-2',
                      isUserMenuOpen && desktopNavPillItemActiveClass
                    )}
                  >
                    <span className="hidden min-w-0 flex-col text-left sm:flex">
                      <span className="max-w-[10rem] truncate transition-colors group-hover:text-gray-700">
                        {userProfileName}
                      </span>
                    </span>
                    <span className="inline-flex size-4 shrink-0 text-neutral-10 opacity-50 transition-colors group-hover:text-gray-700 group-hover:opacity-90">
                      <ArrowDownIcon className="size-4" />
                    </span>
                  </button>
                </div>

                {isUserMenuOpen && (
                  <div className="absolute right-0 top-full z-60 mt-2">
                    <ProfileSection
                      user={auth.user}
                      isUserAnalyticsEnabled={isUserAnalyticsEnabled}
                      onClose={() => setIsUserMenuOpen(false)}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {isCommandPaletteEnabled &&
        isCommandPaletteOpen &&
        createPortal(
          <Suspense fallback={<div>Loading...</div>}>
            <CommandPalette onClose={() => setIsCommandPaletteOpen(false)} />
          </Suspense>,
          document.body
        )}
    </>
  )
}

export default memo(Navigation)
